import type { GettingStartedTarget } from 'app/features/admin/widgets/widgets.model';

/**
 * Types owned by `GettingStartedService` (#20703). The admin-authored config shapes live in
 * `app/features/admin/widgets/widgets.model` (`GettingStartedConfig` and friends); everything
 * here is runtime/per-user state and query bookkeeping that only the service needs.
 */

/** Key of the per-user bucket inside `UserPreferances.ui`. */
export const GETTING_STARTED_UI_KEY = 'gettingStarted';

/**
 * `widgetConfig.template` value that selects the getting-started variant.
 *
 * Deliberately re-declared here instead of importing `GettingStartedTemplateEnum` from
 * `admin/global-widgets/details/getting-started.fields.ts`: that file is a Formly field factory
 * for the admin screen, and pulling it into the shared runtime would drag the whole form config
 * (and `@ngx-formly`) into every lazy chunk that mounts the panel.
 */
export const GETTING_STARTED_TEMPLATE = 'getting-started';

/**
 * Per-user, per-widget state. Persisted under `ui.gettingStarted[widgetModuleId]`.
 *
 * Completed steps are NOT in here — they are derived live from data on every evaluation pass, so
 * deleting your only asset immediately un-ticks the step again.
 *
 * `manualDoneStepIds` is the one exception, and it has to be: a `completion.type: 'manual'` step
 * has no data to derive from by definition ("only tickable by the user"), so the tick itself IS
 * the user choice — the same category as skip and dismiss. It is stored separately from
 * `skippedStepIds` because the two mean different things in the UI (afgerond vs overgeslagen) and
 * because a step may flip from `manual` to `entriesExist` in the config without the stored ticks
 * silently faking real data.
 */
export interface GettingStartedUserState {
  /** Step ids the user chose to skip. */
  skippedStepIds: string[];
  /** Step ids of `completion.type: 'manual'` steps the user ticked off. */
  manualDoneStepIds: string[];
  /** Panel collapsed (header only). Defaults to true. */
  collapsed: boolean;
  /** The user closed the panel entirely. Only honoured when `config.dismissible`. */
  dismissed?: boolean;
}

/** `ui.gettingStarted`, keyed by the widget's module id. */
export type GettingStartedUiBucket = Record<string, GettingStartedUserState>;

/** Which entry the Start button hands to an interface action as row context. */
export type GettingStartedEntryContext = NonNullable<GettingStartedTarget['entryContext']>;

// Count queries and their results now live with the evaluator that owns them:
// `ExternalEntriesQuery` in shared/services/external-entries-evaluator.service.ts. An `entriesExist`
// step is translated into one of those, so both authoring shapes share a single request pass.
